// How the CUSTOMER is billed for time on site past a technician visit's five hours (Session TV2, Director ruling of
// 29 October 2026). Shown on the existing service record panel for a technician visit that has not been approved yet.
// The default is by the hour; the office may instead choose a further day. The choice is a fact the Xero draft is built
// from when the visit is approved, so it is changeable only until then (the route refuses afterwards and the panel stops
// offering it). The subcontractor pay side is a different thing and is not touched here.
//
// No new page, no menu item: two quiet buttons in the record's own action area (the calm screen rule). A press writes
// at once, because it is reversible with one press until approval and nothing is sent to anyone; the route is the
// same one the Guvnor tool previews first.
import { useState } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { TECH_VISIT_DAY_CAP_HOURS, TECH_VISIT_DAY_RATE_LABEL, TECH_VISIT_OVERAGE_HOUR_LABEL, TECH_VISIT_OVERAGE_STEP_MINUTES } from "../../lib/techVisitRate";

export function TechVisitOverageControl({ jobId, furtherDay, onChanged }: { jobId: string; furtherDay: boolean; onChanged?: () => void }) {
  const [current, setCurrent] = useState(furtherDay);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(next: boolean) {
    if (next === current || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.schedule.techVisitOverage(jobId, { furtherDay: next, preview: false });
      setCurrent(res.furtherDay);
      onChanged?.();
    } catch (err) {
      setError(friendlyApiError(err, "Could not record that choice. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  const btn = (active: boolean) =>
    `rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition disabled:opacity-50 ${
      active ? "border-ele-green bg-ele-green/10 text-ele-strong" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
    }`;

  return (
    <div className="mb-3 text-sm text-ele-body" data-testid="tech-visit-overage">
      <p>
        Time on site beyond {TECH_VISIT_DAY_CAP_HOURS} hours is billed to the customer on top of the {TECH_VISIT_DAY_RATE_LABEL} day rate, taken from the
        arrival and departure on the sheet.
      </p>
      <p className="mt-1 text-xs">
        On a visit booked over several working days, or a sheet showing more than a day on site, no time beyond {TECH_VISIT_DAY_CAP_HOURS} hours is worked
        out: the draft carries the day rate only and you add any extra time by hand.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2" role="group" aria-label="How time beyond five hours is billed">
        <button type="button" aria-pressed={!current} disabled={busy} onClick={() => void choose(false)} className={btn(!current)}>
          By the hour, {TECH_VISIT_OVERAGE_HOUR_LABEL} an hour in {TECH_VISIT_OVERAGE_STEP_MINUTES}-minute steps
        </button>
        <button type="button" aria-pressed={current} disabled={busy} onClick={() => void choose(true)} className={btn(current)}>
          As a further day, {TECH_VISIT_DAY_RATE_LABEL}
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-1 text-xs text-ele-danger">
          {error}
        </p>
      )}
    </div>
  );
}
